/**
 * useInvoicePdf Hook
 * 
 * This hook manages PDF data processing, URL generation,
 * and cleanup to reduce complexity in components.
 */

import { useEffect, useMemo } from 'react';
import { logger } from '@/utils/logger';

interface UseInvoicePdfProps {
  pdfData?: { data?: string };
}

export const useInvoicePdf = ({ pdfData }: UseInvoicePdfProps) => {
  // Create PDF data URL from the fetched PDF data
  const pdfUrl = useMemo(() => {
    if (pdfData?.data !== undefined && pdfData?.data !== null) {
      try {
        const binary = atob(pdfData.data);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        return URL.createObjectURL(blob);
      } catch (error) {
        logger.error('Error processing PDF data', 'useInvoicePdf', { error });
        return null;
      }
    }
    return null;
  }, [pdfData?.data]);

  // Cleanup PDF URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        try {
          URL.revokeObjectURL(pdfUrl);
        } catch (error) {
          logger.warn('Failed to revoke PDF URL', 'useInvoicePdf', { error });
        }
      }
    };
  }, [pdfUrl]);

  return {
    pdfUrl,
  };
};
